import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Buildings } from '@phosphor-icons/react';
import api, { fileUrl } from '@/api';

const NEW_STARTUP_FLOW_KEY = 'new_startup_program_flow_slug';
const PRE_DASHBOARD_DESTINATION_KEY = 'pre_dashboard_destination';
const PRE_DASHBOARD_INCUBATOR_NAME_KEY = 'pre_dashboard_incubator_name';

const ProgramApplicationConfirmation = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  })();
  const isStartup = storedUser?.role === 'startup';
  const isNewStartupProgramFlow = sessionStorage.getItem(NEW_STARTUP_FLOW_KEY) === slug;

  useEffect(() => {
    let cancelled = false;
    const loadProgram = async () => {
      try {
        const res = await api.get(`/programs/public/${slug}`);
        if (!cancelled) setProgram(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Unable to load application details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadProgram();
    return () => { cancelled = true; };
  }, [slug]);

  const handleContinue = () => {
    if (isNewStartupProgramFlow) {
      sessionStorage.removeItem(NEW_STARTUP_FLOW_KEY);
      sessionStorage.setItem(PRE_DASHBOARD_DESTINATION_KEY, '/startup/dashboard');
      if (program?.fund_name) {
        sessionStorage.setItem(PRE_DASHBOARD_INCUBATOR_NAME_KEY, program.fund_name);
      } else {
        sessionStorage.removeItem(PRE_DASHBOARD_INCUBATOR_NAME_KEY);
      }
      navigate('/pre-dashboard', { replace: true });
      return;
    }

    navigate(isStartup ? '/startup/programs' : '/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7fbfa] dark:bg-[#050807] flex items-center justify-center px-6">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading confirmation...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7fbfa] dark:bg-[#050807] flex items-center justify-center px-5 py-10" data-testid="program-application-confirmation">
      <div className="w-full max-w-[560px] rounded-2xl border border-slate-200/80 bg-white px-6 py-8 shadow-[0_18px_50px_rgba(15,23,42,0.06)] sm:px-9 sm:py-10 dark:border-teal-900/45 dark:bg-[#08110f] dark:shadow-[0_18px_60px_rgba(0,0,0,0.32)]">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle size={30} weight="fill" className="text-emerald-500" />
        </div>

        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
          Application submitted
        </p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-[-0.03em] text-slate-950 sm:text-[34px] dark:text-white">
          Your application is in.
        </h1>
        <p className="mt-3 max-w-[470px] text-sm leading-6 text-slate-600 dark:text-slate-300">
          {program
            ? <>You successfully applied to <strong className="font-semibold text-slate-900 dark:text-white">{program.name}</strong>{program.fund_name ? <> by {program.fund_name}</> : ''}.</>
            : error || 'Your application was submitted successfully.'}
        </p>

        {program && (
          <div className="mt-7 flex items-center gap-3 rounded-xl border border-teal-100 bg-teal-50/60 px-4 py-3.5 dark:border-teal-900/55 dark:bg-teal-950/35">
            {program.fund_logo_url ? (
              <img
                src={fileUrl(program.fund_logo_url)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                <Buildings size={20} weight="bold" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{program.name}</p>
              {program.fund_name && <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{program.fund_name}</p>}
            </div>
          </div>
        )}

        <p className="mt-6 text-xs leading-5 text-slate-500 dark:text-slate-400">
          You can track your applications and explore other programs from FounderBoard.
        </p>

        <button
          type="button"
          onClick={handleContinue}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-teal-700 dark:bg-teal-500 dark:text-slate-950 dark:hover:bg-teal-400"
          data-testid="confirmation-continue"
        >
          {isNewStartupProgramFlow ? 'Continue to FounderBoard' : 'Back to Programs'}
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default ProgramApplicationConfirmation;
